import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { t } from "../../i18n/ar";

interface IScanInputProps {
    value: string;
    onChange: (value: string) => void;
    onSubmit: () => void;
    // Whenever any of these change, focus jumps back to the input
    refocusOn: unknown[];
}

// The barcode gun "types" into this input. It must ALWAYS have focus.
export const ScanInput = forwardRef<HTMLInputElement, IScanInputProps>(
    ({ value, onChange, onSubmit, refocusOn }, ref) => {
        const inner = useRef<HTMLInputElement>(null);
        useImperativeHandle(ref, () => inner.current as HTMLInputElement);

        useEffect(() => {
            inner.current?.focus();
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, refocusOn);

        // If focus is lost for any reason (tab switch, click), grab it back.
        useEffect(() => {
            const onFocusOut = () => setTimeout(() => {
                const active = document.activeElement;
                if (!active || active === document.body) inner.current?.focus();
            }, 50);
            window.addEventListener("focus", onFocusOut);
            return () => window.removeEventListener("focus", onFocusOut);
        }, []);

        return (
            <div className="rounded-2xl border-2 border-slate-200 bg-white p-3 shadow-sm sm:p-4">
                <div className="flex items-center gap-3">
                    <span className="text-4xl" aria-hidden>📷</span>
                    <input
                        ref={inner}
                        type="text"
                        inputMode="numeric"
                        autoComplete="off"
                        autoFocus
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                onSubmit();
                            }
                        }}
                        placeholder={t.scanHere}
                        className="ltr w-full rounded-xl border-2 border-slate-300 px-4 py-3 text-center font-mono text-3xl font-bold tracking-widest text-slate-800 transition-colors placeholder:font-[Cairo] placeholder:text-xl placeholder:tracking-normal placeholder:text-slate-400 focus:border-[#f68b1e] focus:outline-none"
                    />
                </div>
            </div>
        );
    }
);
ScanInput.displayName = "ScanInput";
